/**
 * @namespace Stores
 * @category Stores
 * @module release_notes-store
 * */

import colors from '@/styles/colors.js'
import i18n from '@/i18n'
import { fetchReleaseNotes } from '@/services/release-notes-service'
import moment from 'moment/moment'
import { featureFunctionIds, hasFeature } from '@/config/features'
import loggingService from '@/services/logging-service.js'

function newState() {
  return {
    currentMode: 'VERIFICATION_MODE',
    isDetailsMode: false,
    selectedReleaseNote: null,
    releaseNoteItem: {},
    releaseNotesHistory: [],
    solidBlue: colors.color_main || '#0b72b5', //Fallback to GeoredV3 main color
    currentLocaleCode: i18n.locale,
    debugConsole: location.host.includes(':')
      ? true
      : window.debug === true || false,
  }
}

export default {
  namespaced: true,
  state: newState(),
  getters: {
    currentMode: (state) => {
      return state.currentMode
    },
    isDetailsMode: (state) => {
      return state.isDetailsMode
    },
    selectedReleaseNote: (state) => {
      return state.selectedReleaseNote
    },
    releaseNoteItem: (state) => {
      return state.releaseNoteItem
    },
    releaseNotesHistory: (state) => {
      return state.releaseNotesHistory
    },
    solidBlue: (state) => {
      return state.solidBlue
    },
    currentLocaleCode: (state) => {
      return state.currentLocaleCode
    },
    debugConsole: (state) => {
      return state.debugConsole
    },
  },
  mutations: {
    setReleaseNotesHistory(state, value) {
      state.releaseNotesHistory = value
    },
    setReleaseNoteItem(state, value) {
      state.releaseNoteItem = value
    },
    setCurrentMode(state, value) {
      state.currentMode = value
    },
    setSelectedReleaseNote(state, value) {
      state.selectedReleaseNote = value
    },
    setIsDetailsMode(state, value) {
      state.isDetailsMode = value
    },
    resetStore(state) {
      let s = newState()

      for (let x in s) {
        state[x] = s[x]
      }
    },
  },
  actions: {
    onSettingsModuleReleaseHistoryClick({ dispatch, commit }) {
      dispatch('app/setCurrentFloatingModule', '', { root: true })
      commit('setCurrentMode', 'HISTORY_LIST_BIG_POPUP')

      if (hasFeature('enableAuditReleaseNotes')) {
        loggingService.activityAuditManager.startActivityAudit(
          featureFunctionIds.releaseNotes
        )
      }
    },
    /**
     * On login, check release notes to display.
     * @param state
     * @param commit
     * @return {Promise<void>}
     */
    async checkReleaseNotes({ state, commit }) {
      this.checkPromise && (await this.checkPromise)
      if (state.currentMode === 'VERIFICATION_MODE') {
        await fetchReleaseNotes()

        //Return if popup has been closed by user during session
        const popupClosedByUser = sessionStorage.getItem(
          'releaseNotesPopupClosedByUser'
        )

        if (popupClosedByUser !== null) {
          return
        }

        let releaseNotesHistory = state.releaseNotesHistory

        let filteredWithinDateRange = releaseNotesHistory
          .filter((item) => {
            const startDate = moment(item.startDate)
            const endDate = moment(item.endDate)
            const currentDate = moment()
            return currentDate.isBetween(startDate, endDate)
          })
          .sort((a, b) => {
            return a.updatedAt > b.updatedAt ? -1 : 1 //date DESC
          })

        if (releaseNotesHistory.length === 0) {
          state.debugConsole && console.log('rnf: No release notes found')
          return //No items
        }

        if (filteredWithinDateRange.length === 0) {
          state.debugConsole &&
            console.log(
              'rnf: There is no release note maching the current date',
              releaseNotesHistory.length
            )
          return //No maching items
        }

        let readNotes =
          JSON.parse(localStorage.getItem('readReleaseNotes')) || []
        let filteredUnread = filteredWithinDateRange.filter((item) => {
          return !readNotes.includes(item.id)
        })

        if (filteredUnread.length === 0) {
          state.debugConsole &&
            console.log('rnf: No maching unread release notes')
          return //No maching items
        }

        commit('setReleaseNoteItem', filteredUnread[0]) //Consider the first one as active (arr is sort by date DESC)

        commit('setCurrentMode', 'ACTIVE_SMALL_POPUP') // Switch to small popup mode
      }
    },
    onLogout({ commit }) {
      //Delete sessionStorage item if exists
      const popupClosedByUser = sessionStorage.getItem(
        'releaseNotesPopupClosedByUser'
      )

      if (popupClosedByUser !== null) {
        sessionStorage.removeItem('releaseNotesPopupClosedByUser')
      }
      //Reset store
      commit('resetStore')
    },
  },
}
